import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

export type NotificationPosition = "top" | "bottom";

type NotificationContextProps = {
  contents: ReactNode;
  position: NotificationPosition;
  displayDuration: number;
  isNotificationVisible: boolean;
  showNotification: (
    contents: ReactNode,
    position?: NotificationPosition,
    customDisplayDuration?: number
  ) => void;
  dismissNotification: () => void;
  setNotificationContents: (contents: ReactNode) => void;
};

const initialState: NotificationContextProps = {
  contents: null,
  position: "bottom",
  displayDuration: 3000,
  isNotificationVisible: false,
  showNotification: () => {},
  dismissNotification: () => {},
  setNotificationContents: () => {},
};

const NotificationContext =
  createContext<NotificationContextProps>(initialState);

// Export the provider as we need to wrap the entire app with it
export function NotificationProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  // Visibility
  const [isNotificationVisible, setIsNotificationVisible] =
    useState<boolean>(false);
  // Contents
  const [contents, setNotificationContents] = useState<ReactNode>(null);
  // Position
  const [position, setPosition] = useState<NotificationPosition>("bottom");
  // Timing
  const timer = useRef(null);
  const [displayDuration, setDisplayDuration] = useState<number>(
    initialState.displayDuration
  );

  const dismissNotification = useCallback(() => {
    setIsNotificationVisible(false);
    setDisplayDuration(initialState.displayDuration);
    clearTimeout(timer.current);
  }, []);

  const showNotification = useCallback(
    (
      contents: ReactNode,
      position: NotificationPosition = "bottom",
      customDisplayDuration?: number
    ) => {
      if (timer.current) clearTimeout(timer.current);
      setNotificationContents(contents);
      setPosition(position);
      setIsNotificationVisible(true);
      if (customDisplayDuration) {
        setDisplayDuration(customDisplayDuration);
      }
      timer.current = setTimeout(() => {
        dismissNotification();
      }, customDisplayDuration || displayDuration);
    },
    []
  );

  const memoedValue = useMemo(
    () => ({
      contents,
      setNotificationContents,
      position,
      displayDuration,
      showNotification,
      dismissNotification,
      isNotificationVisible,
    }),
    [isNotificationVisible, contents, displayDuration]
  );

  // Only render the underlying app after we assert for the presence of a current user.
  return (
    <NotificationContext.Provider value={memoedValue}>
      {children}
    </NotificationContext.Provider>
  );
}

export default function useNotification() {
  return useContext(NotificationContext);
}
